import type { SurveyBuilderManager } from '@/state/surveys/useSurveyBuilder';
import type { Survey } from 'types';
import { cn } from 'helpers/cn';
import { Input } from '@/components/ui/input';
import { SURVEY_TRIGGERS } from 'definitions';

type StepTriggerProps = { manager: SurveyBuilderManager };

type TriggerType = Survey['triggerConfig']['type'];

const FIELD_INPUT = 'border-line bg-field text-foreground h-9 rounded-lg text-body';

const COPY: Record<TriggerType, { label: string; hint: string }> = {
  'page view': { label: 'On page view', hint: 'Shows as soon as the page is ready. Highest reach, lowest intent.' },
  delay: { label: 'After a delay', hint: 'Waits for the visitor to settle. A reasonable default.' },
  'exit intent': {
    label: 'On exit intent',
    // Stated because the substitute is not obvious and customers will ask.
    hint: 'Pointer leaving the viewport on desktop; a fast upward scroll on touch, where there is no pointer to track.',
  },
  event: {
    label: 'On a dataLayer event',
    hint: 'Fires when your own analytics event is pushed. Best for post-purchase.',
  },
  'scroll depth': { label: 'At scroll depth', hint: 'Fires once the visitor has read a proportion of the page.' },
};

const defaultsFor = (type: TriggerType): Survey['triggerConfig'] => {
  if (type === 'delay') return { type, seconds: 10 };
  if (type === 'scroll depth') return { type, percent: 50 };
  if (type === 'event') return { type, eventName: '' };
  return { type } as Survey['triggerConfig'];
};

export const StepTrigger = ({ manager }: StepTriggerProps) => {
  const { draft, updateDraft } = manager;
  const trigger = draft.triggerConfig;

  const selectType = (type: TriggerType) => () => {
    updateDraft((state) => ({ ...state, triggerConfig: defaultsFor(type) }));
  };

  const setField = (field: 'seconds' | 'percent' | 'eventName') => (ev: React.ChangeEvent<HTMLInputElement>) => {
    const raw = ev.target.value;
    updateDraft((state) => ({
      ...state,
      triggerConfig: {
        ...state.triggerConfig,
        [field]: field === 'eventName' ? raw : Number(raw),
      } as Survey['triggerConfig'],
    }));
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h2 className="text-foreground m-0 text-lede font-medium">When should it appear?</h2>
        <p className="supporting m-0">A visitor is asked at most once per session, and never again once they answer.</p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2">
        {SURVEY_TRIGGERS.map((type: TriggerType) => (
          <button
            key={type}
            type="button"
            onClick={selectType(type)}
            className={cn(
              'rounded-card border p-3 text-left transition-colors duration-150',
              trigger.type === type
                ? 'bg-sel-bg border-sel-line text-foreground'
                : 'border-line bg-panel2 hover:bg-hover text-foreground',
            )}
          >
            <p className="m-0 text-meta font-medium">{COPY[type].label}</p>
            <p className="supporting m-0 mt-0.5">{COPY[type].hint}</p>
          </button>
        ))}
      </div>

      {trigger.type === 'delay' && (
        <div className="space-y-1.5">
          <label htmlFor="trigger-seconds" className="micro-label block">
            Delay (seconds)
          </label>
          <Input
            id="trigger-seconds"
            type="number"
            min={0}
            max={600}
            value={trigger.seconds}
            onChange={setField('seconds')}
            className={FIELD_INPUT}
          />
        </div>
      )}

      {trigger.type === 'scroll depth' && (
        <div className="space-y-1.5">
          <label htmlFor="trigger-percent" className="micro-label block">
            Scroll depth (%)
          </label>
          <Input
            id="trigger-percent"
            type="number"
            min={1}
            max={100}
            value={trigger.percent}
            onChange={setField('percent')}
            className={FIELD_INPUT}
          />
        </div>
      )}

      {trigger.type === 'event' && (
        <div className="space-y-1.5">
          <label htmlFor="trigger-event" className="micro-label block">
            dataLayer event name
          </label>
          <Input
            id="trigger-event"
            value={trigger.eventName}
            onChange={setField('eventName')}
            placeholder="purchase"
            className={FIELD_INPUT}
          />
        </div>
      )}
    </div>
  );
};
